<template>
	<div class="search">
		<label>
			<input
				type="text"
				placeholder="Search venue, artist, event..."
				autocomplete="off"
				spellcheck="off"
				@keydown.down.prevent="go($event, 'down')"
				@keydown.up.prevent="go($event, 'up')"
				@keydown.esc="searchTerm = ''"
				@focus="$emit('inputfocus')"
				class="with-v-gap"
				:class="{ empty : searchTerm.length < 1, 'with-results' : resultState === 'results' }"
				v-bind:value="searchTerm"
				v-on:input="searchTerm = $event.target.value"
				v-focus>
			<button
				v-if="searchTerm.length > 0"
				class="close"
				@click="clear"></button>
		</label>

		<div v-if="resultState === 'loading'" class="loading"></div>

		<div v-else-if="resultState === 'no-results'" class="no-results">
			Sorry, no results found, please try a different search
		</div>

		<ul class="results" v-else-if="resultState === 'results'">
			<li
				v-for="searchResult in searchResults"
				v-bind:key="searchResult.uri">
				<button
					v-if="searchResult.entityType === 'event'"
					@click="$emit('event-selected', searchResult)"
					@keydown.down.prevent="go($event, 'down')"
					@keydown.up.prevent="go($event, 'up')"
					class="event">
					<DateSummary
						:startIso="new Date(searchResult.startIso)"
						:endIso="new Date(searchResult.endIso)"
						:isRanged="searchResult.isRanged"/>
					<div class="whatwherecta">
						<div class="whatwhere">
							<div>
								{{ searchResult.name }}
							</div>
							<div class="lowlight-text">
								{{ searchResult.venueSummary.name }}<span v-if="searchResult.venueSummary.nameContainsTown !== true">, {{ searchResult.venueSummary.town }}</span>
							</div>
						</div>
						<div v-if="searchResultCanBePromoted(searchResult)" class="cta">
							Promote this event
						</div>
						<div v-else class="cta lowlight-text">
							Too soon
						</div>
					</div>
				</button>

				<button
					v-else
					@click="$emit('owner-selected', searchResult)"
					@keydown.down.prevent="go($event, 'down')"
					@keydown.up.prevent="go($event, 'up')"
					class="owner" >
					<div class="what">
						{{ searchResult.name }}<span v-if="searchResult.town && searchResult.nameContainsTown !== true">, {{searchResult.town}}</span>
					</div>
					<div class="lowlight-text">
						{{ searchResult.numEvents }} events
					</div>
					<div v-if="searchResult.numEvents > 0" class="cta">
						See events {{ searchResult.entityType === 'venue' ? 'at' : 'for' }} this {{ searchResult.entityType }}
					</div>
				</button>
			</li>
		</ul>

		<p class="help-text">
			Can’t find what you’re looking for? Got a question?
			<a target="_blank" href="mailto:backstage@ents24.com">Get in touch</a>
		</p>
	</div>
</template>

<script>
	import DateSummary from './DateSummary'
	import axios from 'axios'
	import debounce from 'lodash.debounce'
	import FormHelper from '../FormHelper.js'
	import { eventIsPromotable } from '../date-utils';

	export default {
		name     : 'search',
		components : {
			DateSummary
		},
		props: [
			'earliestPromotionIso',
			'shortestPromotionWindow'
		],
		data () {
			return {
				searchTerm    : '',
				searchResults : [],
				resultState   : null,
			}
		},

		/**
		 * Add a scroll listener to blur the `input`, hiding on-screen keyboard
		 */
		updated () {
			let ul = this.$el.querySelector('ul');
			ul && ul.addEventListener('scroll', this.handleScroll);
			document.addEventListener('scroll', this.handleScroll);
		},
		methods : {

			/**
			 * User scrolled results - attempt to hide keyboard and bin this listener
			 */
			handleScroll () {
				let ul    = this.$el.querySelector('ul'),
					input = this.$el.querySelector('input')

				ul && ul.removeEventListener('scroll', this.handleScroll);
				document.removeEventListener('scroll', this.handleScroll);

				input && input.blur();
			},

			// Hit the search endpoint when the user types. Moved (XK: from) a keyup event instead of a watch on `searchTerm`
			// because android phones seem to without input & wait for an autocomplete suggestion in some cases (shrug)
			search () {
				if (!this.searchTerm) {
					this.searchResults = [];
					this.resultState = null;
					return;
				}

				this.resultState = 'loading';
				this.getResults();
			},

			getResults: debounce(function() {
				axios.get(this.$baseApiUrl + '/search?type=&method=search&page=1&start=0&limit=25&&q=' + this.searchTerm)
					.then(response => {
						this.searchResults = response.data.data;

						this.$ga.event(
							this.$gaEventCategory,
							'Search query',
							this.searchTerm
						);
					})
					.finally(() => this.resultState = this.searchResults.length > 0 ? 'results' : 'no-results')
			}, 500),

			searchResultCanBePromoted(searchResult) {
				return eventIsPromotable(searchResult, this.earliestPromotionIso, this.shortestPromotionWindow)
			},

			selectEvent (event) {
				this.selectedEvent = event;
				this.$emit('event-selected', event)
			},

			go (event, direction) {
				FormHelper.go(event, direction, this.$el);
			},

			clear () {
				this.searchResults = [];
				this.searchTerm = '';
				this.resultState = null ;

				setTimeout(() => this.$el.querySelector('input').focus(), 100);
			},
		},
		watch: {
			searchTerm() {
				this.search()
			}
		}
	}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/var';

// Top level block containing <input>
.search {
	position : relative; // List uses absolute position
	margin   : 0 $gap-default;

	&::placeholder {
		color : $color-grey-v-light;
	}

	&:focus {
		box-shadow : 0 0 0 1px $color-ents24-sunset-salmon inset;
	}

	label {

		input {
			background    : $color-black;
			border-radius : 30px;
			box-sizing    : border-box;
			color         : $color-white;
			display       : block;
			margin        : auto;
			text-align    : center;
			width         : 100%;
			padding       : $gap-default;

			&.empty {
				background : $color-black url(../../assets/img/search.svg) 95% 50% no-repeat;
			}

			&.with-results {
				border-radius : 30px 30px 0 0;
			}
		}

		button.close {
			top   : 20px;
			right : 38px;
		}
	}
}

ul.results {
	background : $color-black;
	max-height : 100vh;
	overflow-y : scroll;
	width      : 100%;
	box-sizing : border-box;

	li {
		border-bottom : solid 1px $color-grey-dark;

		.date-summary {
			margin-right : $gap-default;
		}

		button {
			color   : $color-white;
			display : block;
			padding : .75rem;
			width   : 100%;

			&:hover, &:focus {
				background : $color-grey-dark;
			}

			&.event {
				display     : flex;
				align-items : center;

				.whatwherecta {
					flex           : 1;
					flex-direction : column;
					display        : flex;
					padding-top    : 2px; // Align 3 parts w/ calendar

					@media (min-width: 400px) {
						flex-direction: row;
						align-items: center;
					}
				}

				.whatwhere {
					flex : 1;
					margin-right : $gap-default;
				}
			}

			&.owner {
				.what {
					font-size : 1.5rem;
				}
			}
		}
	}
}

// e.g. "Events for <strong>Thekla</strong>, Bristol"
.for {
	margin-bottom : $gap-default;

	a {
		color : $color-white;
	}

	button.close {
		width  : 15px;
		height : 15px;
	}
}

ul,
.selected-entity {
	text-align : left;
}

.selected-entity {
	background-color : $color-black;
	margin-bottom    : $gap-large;
}

.cta {
	color       : $color-ents24-sunset-salmon;
	margin      : 0;
	text-align  : right;
	font-style  : italic;
	justify-self: flex-end;
}

.lowlight-text {
	color : $color-grey-mid;
}

.no-results {
	padding: $gap-large;
}

.help-text {
	margin-top: $gap-large;
}
</style>
